import React, { useCallback, useEffect, useRef } from 'react'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import styled from 'styled-components'
import { rem } from 'polished'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { RouteComponentProps, withRouter } from 'react-router'
import { urlTransform } from 'lib/string/removeSpaceUtils'
import HeadData from 'components/Common/HeadData'
import type { SearchFormRef } from 'components/Search'
import config from 'constants/config'
import { getDestinationProductPage, getDescription, getHeadTitle, getTitle, getRelatedTravelItems, decodeUrlParams, getSubheading, PRODUCT_TYPE_HOTEL, restoreCapitalisation } from 'lib/offer/destinationProductPageUtils'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import Error404Page from 'components/Pages/Error404Page'
import HttpStatus from 'components/Common/HttpStatus'
import SimpleRelatedTravelItems from 'components/OfferPage/SimpleRelatedTravelItems'
import { fetchPlaceByName } from 'actions/PlaceActions'
import { fetchRelatedTravelDestinations } from 'actions/DestinationProductPageActions'
import Spacing from 'components/Common/Spacing'
import Faq from 'components/Common/Faq'
import Breadcrumbs from 'components/Pages/DestinationProductPage/Breadcrumbs'
import SearchForm from 'components/Search/SearchForm'
import { isSearchEnabled } from 'lib/search/searchUtils'
import HideNortonBadge from 'components/Common/HideNortonBadge'
import { isDynamicSearchFooterBoxContent } from 'selectors/featuresSelectors'

import DestinationPageRelatedTravelItems from './Support/DestinationPageRelatedTravelItems'
import TopDestinationList, { DestinationNavItemType } from 'components/Common/TopDestinationsList/TopDestinationList'
import { useGlobalHotelsSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import OfferList from 'components/OfferList/OfferList'
import StandaloneLocationSeededYmalList from 'components/Recommendations/LocationSeeded/StandaloneLocationSeededYmalList'
import OfferListFullWidthPanel from 'components/OfferList/OfferListFullWidthPanel'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import GlobalMobileSearchHeroBar from 'components/SearchV2/Components/Mobile/GlobalMobileSearchHeroBar'
import useOfferList from 'hooks/Offers/useOfferList'
import useOffer from 'hooks/Offers/useOffer'
import DestinationOfferListAbTestProvider from 'contexts/OfferList/DestinationOfferListAbTestProvider'
import { HOTEL_SEARCH_TYPEAHEAD_TYPES } from 'constants/search'
import HotelSearchTripPromotion from '../HotelSearchPage/HotelSearchTripPromotion'
import { isTripPlannerEnabled } from 'tripPlanner/selectors'
import { DESTINATION_PRODUCT_PAGE_CURATED_TRIP_POSITION } from 'tripPlanner/config'
import LinkBoxes from 'components/Common/LinkBox/LinkBoxes'
import { fetchLinkBox } from 'actions/ContentActions'
import { getListName } from 'analytics/enhanced-ecommerce'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'

const Header = styled.div`
  margin: ${rem(40)} auto;

  & > h1, & > h2 {
    margin: 0 auto;
    padding: 0;
  }
`

function FaqWrapper({ children }) {
  return <Pane>
    <PaneBody size="xl-y">
      <LayoutContainer>
        <Heading variant="heading3" align="center" as="h3">Frequently Asked Questions</Heading>
        <Spacing mt={40}>
          {children}
        </Spacing>
      </LayoutContainer>
    </PaneBody>
  </Pane>
}

const TopDestinationListContainer = styled(Pane)`
 padding: ${rem(16)} 0 ${rem(24)};
`

const SearchContainer = styled.div`
  margin-top: ${rem(16)};
  padding: ${rem(16)};
  background: ${({ theme }) => theme.palette.neutral.default.eight};
  border-radius: ${props => props.theme.borderRadius.M};
`

type MatchProps = {
  location?: string;
  holidayType?: string;
  productType: string;
  locationOrHolidayType?: string;
  country?:string;
  state?:string;
  city?:string;
}

interface Props extends RouteComponentProps<MatchProps> {
  regionCode: string;
  isSearchEnabled: boolean;
  locationToPlace: { [location: string]: App.Place };
  placesLoading: { [location: string]: boolean };
  customHeadings: App.ContentHeadings;
  tripPlannerEnabled: boolean;
  isDynamicSearchFooterBoxContent: boolean
}

function DestinationProductPage({
  regionCode,
  match,
  isSearchEnabled,
  locationToPlace,
  placesLoading,
  customHeadings,
  tripPlannerEnabled,
  isDynamicSearchFooterBoxContent,
}: Props) {
  const {
    location,
    holidayType,
    productType,
    shouldRedirect,
  } = decodeUrlParams(match.params)

  const formRef = useRef<SearchFormRef>(null)

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (productType === 'Hotels') {
      dispatch(fetchRelatedTravelDestinations(location))
      dispatch(fetchLinkBox())
    }
  }, [productType, location, dispatch])

  const onBedbankSelectDatesPrompt = useCallback(() => {
    formRef.current.form.current.scrollIntoView()
    formRef.current.openDatePickerMenu()
  }, [])

  const { globalHotelsSearchDispatch, globalHotelsSearchState } = useGlobalHotelsSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchType: 'destination',
    onBedbankSelectDatesPrompt,
  })

  const place = locationToPlace[location]
  const placeLoading = placesLoading[location]
  const placeId = place?.id
  const canonicalName = place?.canonicalName

  const parseCanonicalName = (canonicalName:string) => {
    const splittedCanonicalName = canonicalName.split(', ')
    return (splittedCanonicalName.length === 1 ? 'country' : 'location') + '/' + splittedCanonicalName.join('/')
  }

  useEffect(() => {
    if (!place && location) {
      dispatch(fetchPlaceByName(location))
    }
  }, [dispatch, location, place])

  const tracking = useOfferListTracking(
    getListName('OfferList', 'Destination Product', 'Main Results'),
    {
      ga: {
        category: 'OfferList',
        label: 'Main Results',
      },
    },
  )

  const types = getDestinationProductPage(location, holidayType, productType).pageTypes

  const filters: App.OfferListFilters = {
    holidayTypes: getDestinationProductPage(location, holidayType, productType)?.holidayTypes,
    destinationId: location ? placeId : undefined,
    offerTypes: types,
    distanceEq: 0,
    rooms: [{ adults: 2 }],
  }
  const offerList = useOfferList(filters)
  const [offer] = useOffer(offerList.offerIds[0])

  if (productType === 'Hotels' && canonicalName && canonicalName.toLowerCase() !== location) {
    return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}/hotels/${urlTransform(parseCanonicalName(canonicalName).toLowerCase())}`}/>
  }

  if (productType === 'Hotels' && location && (shouldRedirect || holidayType)) {
    return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}/hotels/${urlTransform(parseCanonicalName(location.toLowerCase()))}`}/>
  }

  if (shouldRedirect) {
    if (productType === 'Hotels' && location) {
      return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}/hotels/${urlTransform(parseCanonicalName(location.toLowerCase()))}`}/>
    }
    // These can be removed once we have ported all the product types to the above
    // https://github.com/lux-group/svc-public-offer-feed/blob/ad20cba1c56afb21b630d85b9ff4472f64c9ed7e/src/context/destinationProductPages.js#L10437-L10442
    if (match.params.locationOrHolidayType !== undefined) {
      return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}/${match.params.locationOrHolidayType.toLowerCase()}.${match.params.productType?.toLowerCase()}`} />
    }
    return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}/${match.params.location?.toLowerCase()}.${match.params.holidayType?.toLowerCase()}.${match.params.productType?.toLowerCase()}`} />
  }

  if (place === null || productType === undefined || (location === undefined && holidayType === undefined)) {
    return <HttpStatus code={404}><Error404Page /></HttpStatus>
  }

  const headTitle = getHeadTitle(location, holidayType, productType, canonicalName)
  const title = customHeadings?.heading ?? getTitle(location, holidayType, productType, canonicalName)
  const metaDescription = getDescription(location, holidayType, productType, canonicalName)
  const relatedTravelItems = getRelatedTravelItems(location, holidayType, productType, canonicalName)
  const subheading = customHeadings?.subHeading ?? getSubheading(location, productType, canonicalName)
  const initialLocation = restoreCapitalisation(location, canonicalName)
  const isCountry = !match.params.city && match.params.country
  const isCity = match.params.city

  if (!place || productType !== PRODUCT_TYPE_HOTEL) {
    filters.locations = [location]
    filters.destinationId = undefined
    filters.rooms = undefined
  }

  let navItems: Array<DestinationNavItemType>
  if (isCountry) navItems = ['Countries', 'Popular', 'Cities']
  if (isCity) navItems = ['Cities', 'Popular', 'Countries']

  return <div>
    <HideNortonBadge />
    <GlobalSearchContextProvider
      state={globalHotelsSearchState}
      dispatch={globalHotelsSearchDispatch}
    >
      <HeadData
        title={headTitle + ' - ' + config.title + ' ' + regionCode}
        description={metaDescription}
        canonical={productType.toLowerCase() === 'hotels' && location ? `/${productType.toLowerCase()}/${urlTransform(parseCanonicalName(location))}` : undefined}
        imageId={offer && 'images' in offer && offer.images[0] ? offer.images[0].id : undefined}
      />
      <Breadcrumbs location={location} holidayType={holidayType} productType={productType} regionCode={regionCode} />
      <Header>
        <Heading variant="heading2" align="center" format="titlecase" as="h1">{title}</Heading>
        <BodyText variant="large" align="center" as="h2">{subheading}</BodyText>
      </Header>
      <LayoutContainer>
        <VerticalSpacer gap={16}>
          {isSearchEnabled && productType !== 'Tours-&-Cruises' && (
            <SearchContainer>
              <SearchForm
                    submitOnApply
                    initialLocation={initialLocation}
                    ref={formRef}
                    showDateFieldOnMobile={false}
                    typeaheadTypes={HOTEL_SEARCH_TYPEAHEAD_TYPES}
                  />
              <GlobalMobileSearchHeroBar placeholder="Search hotel offers in" placeholderTextList={['Bali', 'Fiji', 'Australia', 'Thailand', 'Anywhere']} />
            </SearchContainer>
          )}
          {!placeLoading && (
            <OfferListEventsProvider tracking={tracking}>
              <DestinationOfferListAbTestProvider sourceType="destination-product">
                <OfferList
                  filters={filters}
                  tileStyle="search"
                  interstitials={[{
                    id: 'no-offers',
                    element: <BodyText variant="large" as="h2" align="center">No offers available</BodyText>,
                    show: 'hasNoOffers',
                    position: 'start',
                  },
                  {
                    id: 'recommendations',
                    position: 'end',
                    element: <OfferListFullWidthPanel variant="light">
                      <StandaloneLocationSeededYmalList analyticPageSource="Destination Product" placeName={initialLocation} destinationId={placeId} />
                    </OfferListFullWidthPanel>,
                    show: !!placeId,
                  },
                  {
                    id: 'curated-trip-promo',
                    position: Math.min(offerList.offerIds.length, DESTINATION_PRODUCT_PAGE_CURATED_TRIP_POSITION),
                    element: <HotelSearchTripPromotion placeId={placeId} placeName={initialLocation} />,
                    show: tripPlannerEnabled && !!placeId,
                  },
                  ]}
                />
              </DestinationOfferListAbTestProvider>
            </OfferListEventsProvider>
          )}
        </VerticalSpacer>
      </LayoutContainer>
      {!isDynamicSearchFooterBoxContent && productType !== 'Hotels' && relatedTravelItems.length > 0 && <SimpleRelatedTravelItems items={relatedTravelItems} />}
      {!isDynamicSearchFooterBoxContent && productType === 'Hotels' && location && navItems.length > 0 && <TopDestinationListContainer>
        <PaneBody>
          <TopDestinationList destinationNavItems={navItems}/>
        </PaneBody>
      </TopDestinationListContainer>}
      {isDynamicSearchFooterBoxContent && <LinkBoxes />}
      {location && <Faq
        type="destinationProduct"
        maxToShow={10}
        slug={`${urlTransform(location).toLowerCase()}`}
        tags={[productType]}
        wrapper={FaqWrapper}
      />}
      {productType === 'Hotels' && relatedTravelItems.length > 1 && <DestinationPageRelatedTravelItems items={relatedTravelItems} />}
    </GlobalSearchContextProvider>
  </div>
}

const mapStateToProps = (state: App.State) => ({
  locationToPlace: state.destination.locationToPlace,
  placesLoading: state.destination.placesLoading,
  regionCode: state.geo.currentRegionCode,
  isSearchEnabled: isSearchEnabled(),
  customHeadings: state.content?.secureHeadingParams?.landing,
  tripPlannerEnabled: isTripPlannerEnabled(state),
  isDynamicSearchFooterBoxContent: isDynamicSearchFooterBoxContent(state),
})

export default withRouter(connect(mapStateToProps)(DestinationProductPage))
